import { BRANCH_STATUS_MAP, DOCUMENT_TITLE_TYPE } from "../data/constants";
import store from "../redux/store";
import { getLocalStorage } from "../utils";
import { APP_SOURCE, APP_SOURCE_TYPES, IS_SUPPORT_LOGIN } from "../data/local-storage-constants";
import _, { cloneDeep, isEmpty } from "lodash";
import { ACCOUNTING_TABS_MAP } from "../data/accounting-constants";
import { haveReconEngineAccess } from "./accounting-helper";
import { SubTabsDataType } from "../layouts/header";

export const setDocumentTitle = (titleText: string) => {
  document.title = titleText;
  return () => {
    document.title = DOCUMENT_TITLE_TYPE.default;
  };
};

/**
 * This function will return the list of active branches
 * @param {*} allowedBusinessBranchesDataMap
 */
export const getActiveBranches = (allowedBusinessBranchesDataMap: any) => {
  return Object.keys(allowedBusinessBranchesDataMap).reduce((preState: any, curKey: string) => {
    let curState = cloneDeep(preState);
    if (allowedBusinessBranchesDataMap[curKey].status === BRANCH_STATUS_MAP.ACTIVE) {
      curState[curKey] = allowedBusinessBranchesDataMap[curKey];
    }
    return curState;
  }, {})

};

/**
 * This function will return the true if given feature is allowed for current user
 * @param {*} feature
 */
export const isFeatureAllowed = (feature: string) => {
  let allowedFeatures =
    store.getState().businessReducer?.userData?.allowedFeatures || [];
  return allowedFeatures?.length && allowedFeatures.includes(feature);
};

/**
 * This function will filter out the options with are not allowed for the user
 * @param {*} tooltipOptionsMap
 */
export const getFilteredTooltipOptions = (tooltipOptionsMap: Record<string, SubTabsDataType>): Record<string, SubTabsDataType> => {
  const { userData, businessData, licences = [] } = store.getState().businessReducer;
  let filteredMap: any =
    Object.values(tooltipOptionsMap)?.reduce((prevVal, tabItem) => {
      let featureTypes = tabItem?.feature;
      let { isShow = true } = tabItem;
      //check for user email
      if (isShow && tabItem.emailIds && !isEmpty(tabItem.emailIds)) {
        isShow = tabItem.emailIds.findIndex(userEmail => userEmail === userData?.contactInfo?.email) !== -1;
      }
      //check for licence
      const licences_list = licences.map((licence: { licenceCode: string }) => licence.licenceCode);
      if (isShow && tabItem.licences && !isEmpty(tabItem.licences)) {
        isShow = tabItem.licences.findIndex(licence => licences_list.includes(licence)) !== -1;
      }
      //check for businessName
      if (isShow && tabItem.businessNames && !isEmpty(tabItem.businessNames)) {
        isShow = tabItem.businessNames.findIndex(businessName => businessName === businessData?.businessName) !== -1;
      }
      let checkMultipleFeature = false;

      if (Array.isArray(featureTypes)) {
        featureTypes.forEach((element: any) => {
          if (isFeatureAllowed(element)) {
            checkMultipleFeature = true;
          }
        });
      }
      if (
        tabItem.permission &&
        tabItem.permission?.length &&
        !tabItem.permission.includes(userData.role)
      ) {
        isShow = false;
      }
      if (
        (!tabItem?.feature ||
          isFeatureAllowed(Array.isArray(tabItem?.feature) ? '' : tabItem?.feature) ||
          checkMultipleFeature) &&
        isShow
      ) {
        prevVal[tabItem.tabId] = tabItem;
      }
      return prevVal;
    }, {} as Record<string, SubTabsDataType>) || {};
  return filteredMap;
};

export const isSourcePos = () =>
  getLocalStorage(APP_SOURCE) === APP_SOURCE_TYPES.POS;

export const closeTab = () => {
  window.opener = null;
  window.open("", "_self");
  window.close();
};

// Function to remove multiple key-value pairs
export const removeKeys = (object: any, keys: any) => {
  const newObj = { ...object };
  keys.forEach((key: any) => {
    delete newObj[key];
  });
  return newObj;
};
/*
  returns form data with all object key values
*/
export const createFormData = (data: any) => {
  let formData: any = new FormData();
  Object.entries(data).forEach((val: any, index: any) => {
    formData.append(val[0], val[1]);
  });
  return formData;
};

/**
 * Checks whether the passed value is string
 * @param inputVal input value
 * @returns true or false based on whether given value is a string or not
 */
export const isString = (inputVal: any) => {
  return typeof inputVal === "string";
};

/**
 * Checks whether the passed value is a object
 * @param inputVal input value
 * @returns true or false based on whether given value is a object or not
 */
export const isObject = (inputVal: any) => {
  return inputVal === Object(inputVal);
};

/**
 * Checks whether the passed string is empty
 * @param inputStr input string
 * @returns true or false based on whether given string is empty
 */
export const isStringEmpty = (inputStr: string) => {
  if (!inputStr || (isString(inputStr) && inputStr.trim().length < 1)) {
    return true;
  }
  return false;
};

/**
 * Checks whether the passed object is empty
 * @param inputVal input value
 * @returns true or false based on whether given object is empty
 */
export const isObjectEmpty = (inputVal: any) => {
  for (let prop in inputVal) {
    if (inputVal.hasOwnProperty(prop)) return false;
  }
  return true;
};

/**
 * removes keys with null, undefined, empty, etc values
 * @param inputVal input value
 */
const removeFalsyValuesUtil = (inputVal: any) => {
  for (let i in inputVal) {
    if (isObject(inputVal[i])) {
      removeFalsyValuesUtil(inputVal[i]);
    }
    if (
      inputVal[i] === null ||
      inputVal[i] === undefined ||
      (inputVal[i].trim && isStringEmpty(inputVal[i])) ||
      (isObject(inputVal[i]) && isObjectEmpty(inputVal[i]))
    ) {
      delete inputVal[i];
    }
  }
  return inputVal;
};

/**
 * removes keys with null, undefined, empty, etc values
 * @param inputVal input value
 */
export const removeFalsyValues = (inputVal: any) => {
  let returnVal = removeFalsyValuesUtil(cloneDeep(inputVal));
  return returnVal;
};

/**
 * round number upto two decimal point
 * @param inputVal input value
 */
export const round2 = (inputVal: any) => {
  if (isNaN(inputVal)) {
    return "-";
  }
  return Math.round((inputVal + Number.EPSILON) * 100) / 100;
};

/**
 * send color code via random number
 * @param inputVal input value
 */
export const generateRandomColorCode = (input: number) => {
  switch (input % 10) {
    case 1:
      return "#2A77F2";
    case 2:
      return "#575757";
    case 3:
      return "#4E875B";
    case 4:
      return "#6283AC";
    case 5:
      return "#E7B73D";
    case 6:
      return "#0088FE";
    case 7:
      return "#00C49F";
    case 9:
      return "#FFBB28";
    default:
      return "#FF8042";
  }
};

export const isSupportLogin = () => {
  const isSupportLogin = getLocalStorage(IS_SUPPORT_LOGIN);
  return isSupportLogin ? JSON.parse(isSupportLogin) : false;
};

export const checkSupportLogout = () => {
  if (isSupportLogin()) {
    localStorage.clear();
  }
};
//Function to change custom dates in date range selector
export function getOptionForDates(customDisabledDates: any) {
  return [
    {
      value: "Last 7 days",
      totalDays: 7,
    },
    {
      value: "Last 15 days",
      totalDays: 15,
    },
    {
      value: "Last 30 days",
      totalDays: 30,
    },
    {
      value: "Last 60 days",
      totalDays: 60,
    },
    {
      value: "Last 90 days",
      totalDays: 90,
    },
    {
      value: "Since inception",
      totalDays: customDisabledDates,
    },
  ];
}

export function getLowercaseKey(value: string): string {
  return value.toLowerCase().trim().replace(" ", "_");
}

/**
 * converts string array to map of objects
 * @param inputArray input array of string
 */
export const convertStrArrToMapOfObj = (
  inputArray: string[]
): Record<string, { key: string; label: string }> => {
  let result: Record<string, { key: string; label: string }> = {};
  for (let label of inputArray) {
    if (!_.isString(label) || !label.trim()) continue;
    let key = getLowercaseKey(label);
    result[key] = {
      key,
      label,
    };
  }
  return result;
};


/**
 * This function will convert array to object using key
 * @param {*} inputArr
 */
export function convertArrayToObject<T>(
  inputArr: T[],
  uniqueKey: keyof T & string,
  convertLowerCase = true,
): Record<string, T> {
  const lowerCase = (key: string) => {
    if (!convertLowerCase) return key;
    return key && key.toLocaleLowerCase();
  }
  return inputArr.reduce((acc, cur) => {
    return { ...acc, [lowerCase(cur[uniqueKey] as unknown as string)]: cur };
  }, {} as Record<string, T>);
}

/**
 * Generates a random float between two numbers.
 * @param upperLimit - The upper limit of the range.
 * @param lowerLimit - The lower limit of the range.
 * @returns A random float between lowerLimit and upperLimit.
 */
export function generateRandom(upperLimit: number, lowerLimit = 0): number {
  return Math.random() * (upperLimit - lowerLimit) + lowerLimit;
}
/**
 * 
 * @param num 
 * @param precision 
 * @returns dynamically adjusts the precision based on whether the number has a decimal part
 */
export function formatNumber(num: number, precision = 2) {
  return Number.isInteger(num) ? num.toString() : num.toFixed(precision);
}

/**
 * 
 * @param branches 
 * @param branchLabels 
 * @returns branches with matching branch labels
 */
export function filterBranchUsingLabel(branches: Record<string, any>[], branchLabels: string[]) {
  if (!branchLabels?.length) return [];
  return branches.filter(branch => branch.branchLabels.some((label: string) => branchLabels.includes(label)));
}